import React from 'react';
import { XIcon, PackageIcon } from 'lucide-react';
import Card from '../ui/Card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
interface ProductLineBreakdownProps {
  data: any;
  onClose: () => void;
}
const ProductLineBreakdown: React.FC<ProductLineBreakdownProps> = ({
  data,
  onClose
}) => {
  const productData = {
    name: data?.productLine || 'Lighting',
    costBreakdown: [{
      component: 'Raw Materials',
      cost: 425,
      percentage: 58.2
    }, {
      component: 'Labor',
      cost: 95,
      percentage: 13.0
    }, {
      component: 'Manufacturing OH',
      cost: 68,
      percentage: 9.3
    }, {
      component: 'Quality Control',
      cost: 32,
      percentage: 4.4
    }, {
      component: 'Packaging',
      cost: 28,
      percentage: 3.8
    }, {
      component: 'Logistics',
      cost: 45,
      percentage: 6.2
    }, {
      component: 'Other',
      cost: 37,
      percentage: 5.1
    }],
    marginAnalysis: [{
      metric: 'Selling Price',
      value: 1000
    }, {
      metric: 'Total Cost',
      value: 730
    }, {
      metric: 'Gross Margin',
      value: 270
    }, {
      metric: 'Operating Margin',
      value: 185
    }, {
      metric: 'Net Margin',
      value: 152
    }],
    pieData: [{
      name: 'Raw Materials',
      value: 425,
      color: '#3b82f6'
    }, {
      name: 'Labor',
      value: 95,
      color: '#10b981'
    }, {
      name: 'Manufacturing',
      value: 68,
      color: '#f59e0b'
    }, {
      name: 'Other',
      value: 142,
      color: '#ef4444'
    }]
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6" data-id="element-11431">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" data-id="element-11432">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between" data-id="element-11433">
          <div className="flex items-center" data-id="element-11434">
            <PackageIcon size={24} className="text-purple-600 mr-3" data-id="element-11435" />
            <div data-id="element-11436">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-11437">
                Product Line: {productData.name}
              </h2>
              <p className="text-sm text-gray-600" data-id="element-11438">
                Cost structure and margin breakdown
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-11439">
            <XIcon size={24} className="text-gray-500" data-id="element-11440" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-11441">
          {/* Cost Breakdown */}
          <Card className="p-6" data-id="element-11442">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-11443">
              Cost Breakdown (₹ per unit)
            </h3>
            <div className="space-y-2" data-id="element-11444">
              {productData.costBreakdown.map((item, index) => <div key={index} className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded" data-id="element-11445">
                  <span className="text-sm text-gray-800" data-id="element-11446">
                    {item.component}
                  </span>
                  <div className="flex items-center space-x-6" data-id="element-11447">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11448">
                      ₹{item.cost}
                    </span>
                    <span className="text-sm text-gray-600 w-16 text-right" data-id="element-11449">
                      {item.percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>)}
            </div>
          </Card>
          {/* Charts */}
          <div className="grid grid-cols-2 gap-6" data-id="element-11450">
            <Card className="p-6" data-id="element-11451">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-11452">
                Cost Distribution
              </h3>
              <ResponsiveContainer width="100%" height={300} data-id="element-11453">
                <PieChart data-id="element-11454">
                  <Pie data={productData.pieData} cx="50%" cy="50%" labelLine={false} label={({
                  name,
                  percent
                }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={100} fill="#8884d8" dataKey="value" data-id="element-11455">
                    {productData.pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-11456" />)}
                  </Pie>
                  <Tooltip data-id="element-11457" />
                </PieChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-6" data-id="element-11458">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-11459">
                Margin Waterfall
              </h3>
              <ResponsiveContainer width="100%" height={300} data-id="element-11460">
                <BarChart data={productData.marginAnalysis} data-id="element-11461">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-11462" />
                  <XAxis dataKey="metric" tick={{
                  fontSize: 11
                }} angle={-15} textAnchor="end" height={80} data-id="element-11463" />
                  <YAxis tick={{
                  fontSize: 12
                }} data-id="element-11464" />
                  <Tooltip data-id="element-11465" />
                  <Bar dataKey="value" fill="#3b82f6" data-id="element-11466" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          {/* Optimization Opportunities */}
          <Card className="p-6 bg-blue-50 border-2 border-blue-200" data-id="element-11467">
            <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-11468">
              Cost Optimization Opportunities
            </h3>
            <ul className="space-y-2 text-sm" data-id="element-11469">
              <li className="flex items-start" data-id="element-11470">
                <span className="text-blue-600 mr-2" data-id="element-11471">→</span>
                <span data-id="element-11472">
                  Raw materials account for 58% of cost - explore alternate
                  suppliers
                </span>
              </li>
              <li className="flex items-start" data-id="element-11473">
                <span className="text-blue-600 mr-2" data-id="element-11474">→</span>
                <span data-id="element-11475">
                  Manufacturing overhead can be reduced by 12% through
                  automation
                </span>
              </li>
              <li className="flex items-start" data-id="element-11476">
                <span className="text-blue-600 mr-2" data-id="element-11477">→</span>
                <span data-id="element-11478">Logistics optimization could save ₹8-12 per unit</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>;
};
export default ProductLineBreakdown;